import { pinnedContracts } from '@hoodmorning/config';
import { nonzeroAddressSchema, uint256StringSchema, type Address, type Hex, type PreparedCall } from '@hoodmorning/contracts';
import {
  decodeFunctionResult,
  encodeFunctionResult,
  encodeFunctionData,
  getAddress,
  isAddress,
  isAddressEqual,
  type PublicClient,
} from 'viem';

import { erc20RecoveryAbi, semiModularAccountAbi } from './abis.js';
import { verifyContractCode, type ContractPin } from './codeVerification.js';
import { verifySmaBRuntime } from './smaBRuntime.js';
import {
  EXPECTED_SMA_B_ACCOUNT_ID,
  preflightSmaBAccount,
} from './vault.js';

type RecoveryReadClient = Pick<
  PublicClient,
  'getBytecode' | 'getChainId' | 'getStorageAt' | 'readContract'
>;

export type RecoveryPreflight = {
  canSubmit: boolean;
  failures: string[];
  warnings: string[];
  chainId: number;
  vault: Address;
  connectedOwner: Address;
  onchainOwner: Address | null;
  accountId: string | null;
  runtimeImplementationReferenceVerified: boolean;
  infrastructureCodeVerified: boolean;
  authorizationPath: 'sma-b-v2.0.0-fallback-owner-direct-execute';
};

const recoveryInfrastructurePins: readonly ContractPin[] = [
  {
    name: 'entryPointV07',
    address: getAddress(pinnedContracts.entryPointV07.address),
    expectedCodeHash: pinnedContracts.entryPointV07.codeHash,
  },
  {
    name: 'smaBImplementation',
    address: getAddress(pinnedContracts.smaBImplementation.address),
    expectedCodeHash: pinnedContracts.smaBImplementation.codeHash,
  },
] as const;

export async function preflightRecovery(
  client: RecoveryReadClient,
  vaultInput: Address,
  connectedOwnerInput: Address,
): Promise<RecoveryPreflight> {
  const vault = getAddress(vaultInput);
  const connectedOwner = getAddress(connectedOwnerInput);
  const base = await preflightSmaBAccount(client, vault, connectedOwner);
  const failures = [...base.failures];
  const warnings = [
    'The owner wallet must hold enough ETH to pay Robinhood Chain gas.',
    'This standalone app does not enumerate optional modules; it never invokes them.',
  ];

  let runtimeImplementationReferenceVerified = false;
  // An absent runtime cannot verify an implementation reference. Keep direct
  // recovery locked without rereading empty code or querying its storage.
  // Every deployed account still receives the full check, even if base failed.
  if (base.deployed) {
    try {
      const runtime = await verifySmaBRuntime(client, vault);
      runtimeImplementationReferenceVerified = runtime.valid;
      failures.push(...runtime.failures);
    } catch {
      runtimeImplementationReferenceVerified = false;
    }
  }
  if (!runtimeImplementationReferenceVerified) {
    failures.push('RUNTIME_IMPLEMENTATION_REFERENCE_UNVERIFIED');
  }

  let entryPointMatches = false;
  if (base.deployed) {
    try {
      const entryPoint = await client.readContract({
        address: vault,
        abi: semiModularAccountAbi,
        functionName: 'entryPoint',
      });
      entryPointMatches = isAddressEqual(entryPoint, pinnedContracts.entryPointV07.address);
      if (!entryPointMatches) failures.push('ENTRY_POINT_MISMATCH');
    } catch {
      failures.push('ENTRY_POINT_UNREADABLE');
    }
  }

  const infrastructure = await verifyContractCode(client, recoveryInfrastructurePins);
  if (!infrastructure.valid) failures.push(...infrastructure.failures);
  if (base.accountId !== EXPECTED_SMA_B_ACCOUNT_ID) failures.push('DIRECT_EXECUTE_ABI_UNVERIFIED');
  if (isAddressEqual(vault, connectedOwner)) failures.push('VAULT_EQUALS_OWNER');

  return {
    canSubmit: failures.length === 0 && entryPointMatches,
    failures: [...new Set(failures)],
    warnings,
    chainId: base.chainId,
    vault,
    connectedOwner,
    onchainOwner: base.owner,
    accountId: base.accountId,
    runtimeImplementationReferenceVerified,
    infrastructureCodeVerified: infrastructure.valid,
    authorizationPath: 'sma-b-v2.0.0-fallback-owner-direct-execute',
  };
}

export type RecoveryAsset =
  | { kind: 'NATIVE' }
  | { kind: 'ERC20'; token: Address };

export type RecoveryTransfer = {
  vault: Address;
  recipient: Address;
  asset: RecoveryAsset;
  amount: bigint;
  call: PreparedCall;
  description: string;
};

export function prepareRecoveryTransfer(input: {
  vault: Address;
  recipient: Address;
  asset: RecoveryAsset;
  amount: bigint;
}): RecoveryTransfer {
  nonzeroAddressSchema.parse(input.vault);
  nonzeroAddressSchema.parse(input.recipient);
  uint256StringSchema.parse(input.amount.toString());
  const vault = getAddress(input.vault);
  const recipient = getAddress(input.recipient);
  if (input.amount <= 0n) throw new RangeError('Recovery amount must be positive');
  if (isAddressEqual(vault, recipient)) throw new Error('Recipient must differ from vault');

  if (input.asset.kind === 'NATIVE') {
    return {
      vault,
      recipient,
      asset: input.asset,
      amount: input.amount,
      call: { to: recipient, value: input.amount, data: '0x' },
      description: `Transfer ${input.amount} wei of native ETH to ${recipient}`,
    };
  }

  if (input.asset.kind !== 'ERC20') throw new Error('Unsupported recovery asset');
  if (!isAddress(input.asset.token)) throw new Error('Invalid ERC-20 address');
  nonzeroAddressSchema.parse(input.asset.token);
  const token = getAddress(input.asset.token);
  return {
    vault,
    recipient,
    asset: { kind: 'ERC20', token },
    amount: input.amount,
    call: {
      to: token,
      value: 0n,
      data: encodeFunctionData({
        abi: erc20RecoveryAbi,
        functionName: 'transfer',
        args: [recipient, input.amount],
      }),
    },
    description: `Call ERC-20 ${token}.transfer(${recipient}, ${input.amount})`,
  };
}

export function encodeDirectOwnerRecovery(
  preflight: RecoveryPreflight,
  transfer: RecoveryTransfer,
): { to: Address; value: 0n; data: Hex } {
  if (!preflight.canSubmit) {
    throw new Error(`Recovery is fail-closed: ${preflight.failures.join(', ')}`);
  }
  if (!isAddressEqual(preflight.vault, transfer.vault)) {
    throw new Error('Transfer vault differs from verified vault');
  }
  const canonical = prepareRecoveryTransfer(transfer);
  if (!isAddressEqual(canonical.call.to, transfer.call.to) || canonical.call.value !== transfer.call.value
    || canonical.call.data.toLowerCase() !== transfer.call.data.toLowerCase()) {
    throw new Error('Recovery call differs from displayed transfer');
  }

  return {
    to: transfer.vault,
    value: 0n,
    data: encodeFunctionData({
      abi: semiModularAccountAbi,
      functionName: 'execute',
      args: [canonical.call.to, canonical.call.value, canonical.call.data],
    }),
  };
}

/** SMA-B execute returns the target's bytes; outer eth_call success alone does
 * not mean an ERC-20 transfer succeeded. Empty legacy returns are supported,
 * but every nonempty return must be the exact ABI encoding of true. This is
 * simulation evidence only; mined transfers need separate effect verification.
 */
export function assertRecoverySimulationResult(transfer: RecoveryTransfer, data: Hex | undefined): void {
  if (transfer.asset.kind === 'NATIVE') return;
  if (data === undefined) throw new Error('ERC-20 recovery simulation returned no execute result');
  const result = decodeFunctionResult({ abi: semiModularAccountAbi, functionName: 'execute', data });
  if (encodeFunctionResult({ abi: semiModularAccountAbi, functionName: 'execute', result }).toLowerCase() !== data.toLowerCase()) {
    throw new Error('ERC-20 recovery simulation returned malformed execute bytes');
  }
  if (result !== '0x' && result.toLowerCase() !== `0x${'0'.repeat(63)}1`) {
    throw new Error('ERC-20 recovery transfer returned false or unsupported data');
  }
}

/** Exact-token recovery only. Fees, rebases, unrelated same-block movements and
 * dishonest balance responses cannot be labelled a verified recovery effect. */
export function assertRecoveryTokenEffect(input: {
  amount: bigint; vaultBefore: bigint; vaultAfter: bigint; recipientBefore: bigint; recipientAfter: bigint;
}): void {
  if (input.amount <= 0n || [input.vaultBefore, input.vaultAfter, input.recipientBefore, input.recipientAfter].some(value => value < 0n)
    || input.vaultBefore - input.vaultAfter !== input.amount || input.recipientAfter - input.recipientBefore !== input.amount) {
    throw new Error('ERC-20 recovery effect unresolved: exact vault debit and recipient credit were not observed');
  }
}
